import { Button, Flex } from "antd";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  useAddWishlistMutation,
  useGetCheckWishlistQuery,
  useRemoveWishlistMutation,
} from "../../app/features/wishlist/wishlist.api";
import SaveIcon from "../../assets/icons/SaveIcon";
import SaveIconActive from "../../assets/icons/SaveIconActive";
import useAuth from "../../hooks/useAuth";
import { toast } from "../../lib/toast";
import { cn } from "../../utils/global.utils";
import { commaNumber, enToBnNumber } from "../../utils/number.utils";

type Props = {
  id: number | string;
  img: string;
  title: string;
  category: string;
  description: string;
  time?: string;
  start?: string;
  job?: string;
  price?: string | number;
  offerPrice?: string | number;
  moreBtnText?: string;
  onMoreButton?: (id: number | string) => void;
  isWish?: boolean;
  onWish?: (type: "wish" | "default", id: number | string) => void;
  tags?: { title: string; icon?: string }[];
  courseUuid?: string;
  organizationUuid?: string;
};

export default function CourseItem({
  id,
  img,
  title,
  category,
  description,
  price = 0,
  offerPrice = 0,
  moreBtnText = "বিস্তারিত দেখুন",
  onMoreButton,
  isWish,
  onWish,
  tags,
  courseUuid,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [addWishlist, { isLoading: addLoading }] = useAddWishlistMutation();
  const [removeWishlist, { isLoading: removeLoading }] =
    useRemoveWishlistMutation();

  const isShowOfferPrice =
    Number(price || 0) > 0 && Number(price || 0) !== Number(offerPrice || 0);

  // Check if course is in wishlist (only if authenticated and has UUID)
  const { data: checkWishlist } = useGetCheckWishlistQuery(
    { courseUuid: courseUuid! },
    { skip: !courseUuid || !isAuthenticated },
  );

  const isWishlisted =
    courseUuid && isAuthenticated ? checkWishlist?.exists : isWish;

  const handleWish = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      if (location.pathname !== "/login") {
        navigate("/login", { state: { from: location } });
      }
      return;
    }

    if (!courseUuid) {
      // Fallback to prop handler if no UUID (e.g. mock data)
      if (onWish) onWish(isWishlisted ? "wish" : "default", id);
      return;
    }

    if (addLoading || removeLoading) {
      return;
    }

    try {
      if (isWishlisted) {
        // message.loading("উইশলিস্ট থেকে সরানো হচ্ছে...");
        // Assuming simple toast usage based on lib definition.
        await removeWishlist({ courseUuid }).unwrap();
        toast("success", "উইশলিস্ট থেকে সফলভাবে সরানো হয়েছে");
      } else {
        // message.loading("উইশলিস্টে যোগ করা হচ্ছে...");
        await addWishlist({
          courseUuid,
          note: "Added from course list",
        }).unwrap();
        toast("success", "উইশলিস্টে সফলভাবে যোগ করা হয়েছে");
      }
    } catch (error) {
      toast("error", "উইশলিস্ট আপডেট করতে ব্যর্থ হয়েছে");
    }
  };

  return (
    <Flex
      className="flex-col rounded-xl shadow cursor-pointer"
      onClick={() => onMoreButton && onMoreButton(id)}
    >
      <div className="h-52 rounded-t-lg overflow-hidden">
        <img
          className="w-full h-full rounded-t-lg object-cover transition-transform hover:scale-110"
          src={img}
          alt=""
        />
      </div>

      <div className="p-4 pb-0">
        <div>
          <Flex justify="space-between" align="center">
            <h3 className="flex-1 min-w-0 text-theme-zinc-700 text-xl font-semibold truncate max-w-72 sm:max-w-full">
              {title}
            </h3>

            <Flex
              align="center"
              justify="center"
              className="w-9 h-6"
              onClick={handleWish}
            >
              {isWishlisted ? (
                <SaveIconActive
                  className={cn(
                    "relative -top-1 cursor-pointer",
                    (addLoading || removeLoading) &&
                      "opacity-50 cursor-not-allowed",
                  )}
                />
              ) : (
                <SaveIcon
                  className={cn(
                    "relative -top-1 cursor-pointer",
                    (addLoading || removeLoading) &&
                      "opacity-50 cursor-not-allowed",
                  )}
                />
              )}
            </Flex>
          </Flex>
          <div className="text-theme-slate-600 text-sm font-normal">
            <p className="mb-2">বিভাগ: {category}</p>
            <p>{description}</p>
          </div>
        </div>

        <Flex wrap align="center" gap={8} className="mt-2.5">
          {/* <Item icon={<TimeIcon className="text-base" />}>{time}</Item>
          <Item icon={<PinIcon className="text-base" />}>{start}</Item> */}
          {tags?.map((tag) => (
            <Item key={tag.title} icon={tag.icon ? tag.icon : undefined}>
              {tag.title}
            </Item>
          ))}
        </Flex>
      </div>

      <Flex
        justify="space-between"
        align="center"
        className="p-4 pt-5 mt-auto"
        gap={6}
        wrap
      >
        <Flex align="center" gap={8}>
          <h6 className="text-red-500 text-2xl font-semibold">
            ৳{enToBnNumber(commaNumber(offerPrice))}
          </h6>
          {isShowOfferPrice && (
            <span className="text-theme-slate-400 text-sm font-medium line-through">
              ৳{enToBnNumber(commaNumber(price))}
            </span>
          )}
        </Flex>

        <Button
          type="primary"
          className="rounded-full pt-1"
          onClick={() => onMoreButton && onMoreButton(id)}
        >
          {moreBtnText}
        </Button>
      </Flex>
    </Flex>
  );
}

type IItem = {
  icon: ReactNode;
  children: ReactNode;
};
const Item = ({ icon, children }: IItem) => (
  <Flex
    align="center"
    gap={6}
    className="px-1.5 py-1 bg-theme-slate-100 rounded-md text-theme-slate-500"
  >
    {icon}
    <span className="text-sm font-medium relative top-0.5">{children}</span>
  </Flex>
);
